import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Home,
  FileText,
  LogOut,
  Settings as SettingsIcon,
  User,
  Users,
  Shield,
  BarChart3,
  CreditCard,
  PlusCircle,
  Menu,
  X,
  Database,
  Building2,
  Boxes,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import {
  clearActiveClient,
  getActiveClientId,
  setActiveClient,
} from "@/utils/clientWorkspace";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [companySettings, setCompanySettings] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessibleClients, setAccessibleClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(getActiveClientId());
  const isAdmin = user.role === "admin";
  const shouldShowClientWorkspace =
    user.account_type === "team_member" || user.account_type === "organization";
  const requiresWorkspaceSelection = user.account_type === "team_member";
  const workspaceSelectValue =
    user.account_type === "organization" && !activeClientId
      ? "__all__"
      : activeClientId;

  const loadCompanySettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/company`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("response", response);
      console.log("response.data", response.data);

      if (response.data && response.data.company_name) {
        setCompanySettings(response.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const loadUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUserProfile(response.data);
    } catch (error) {
      // Profile load failed
    }
  };

  const loadAccessibleClients = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/clients/accessible`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const clients = response.data.clients || [];
      setAccessibleClients(clients);

      if (clients.length === 0) {
        clearActiveClient();
        setActiveClientId("");
        return;
      }

      const storedClientId = getActiveClientId();
      if (user.account_type === "organization" && !storedClientId) {
        setActiveClientId("");
        return;
      }

      const matchedStoredClient = clients.find((client) => client.id === storedClientId);

      if (matchedStoredClient) {
        setActiveClientId(matchedStoredClient.id);
        return;
      }

      if (user.account_type === "organization") {
        clearActiveClient();
        setActiveClientId("");
        return;
      }

      setActiveClient(clients[0]);
      setActiveClientId(clients[0].id);
    } catch (error) {
      console.error("Failed to load accessible clients:", error);
    }
  }, [user.account_type]);

  useEffect(() => {
    loadUserProfile();
    if (!isAdmin && shouldShowClientWorkspace) {
      loadAccessibleClients();
    } else if (!isAdmin) {
      loadCompanySettings();
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isAdmin, shouldShowClientWorkspace, loadAccessibleClients]);

  useEffect(() => {
    if (isAdmin || !shouldShowClientWorkspace) {
      return;
    }

    if (requiresWorkspaceSelection && !activeClientId) {
      setCompanySettings(null);
      setIsLoading(false);
      return;
    }

    loadCompanySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId, isAdmin, shouldShowClientWorkspace, requiresWorkspaceSelection]);

  const handleClientWorkspaceChange = (clientId) => {
    if (clientId === "__all__") {
      clearActiveClient();
      setActiveClientId("");
      window.location.reload();
      return;
    }

    const selectedClient = accessibleClients.find((client) => client.id === clientId);
    if (!selectedClient) return;

    setActiveClient(selectedClient);
    setActiveClientId(selectedClient.id);
    window.location.reload();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    clearActiveClient();
    navigate("/login");
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const navItems = isAdmin
    ? [
        {
          path: "/admin",
          label: "User Management",
          icon: Shield,
          testId: "nav-admin-btn",
        },
        {
          path: "/admin/reports",
          label: "All Invoices",
          icon: BarChart3,
          testId: "nav-admin-reports-btn",
        },
      ]
    : [
        ...((user.account_type === "client" || user.account_type === "team_member")
          ? [
              {
                path: "/customer-master",
                label: "Customer Master",
                icon: Database,
                testId: "nav-customer-master-btn",
              },
            ]
          : []),
        {
          path: "/",
          label: "Dashboard",
          icon: Home,
          testId: "nav-dashboard-btn",
        },
        {
          path: "/invoices",
          label: "Invoices",
          icon: FileText,
          testId: "nav-invoices-btn",
        },
        ...(user.account_type !== "organization"
          ? [{
              path: "/manual-entry",
              label: "Entry",
              icon: PlusCircle,
              testId: "nav-manual-entry-btn",
            }]
          : []),
        {
          path: "/reports",
          label: "Reports",
          icon: BarChart3,
          testId: "nav-reports-btn",
        },
        {
          path: "/item-master",
          label: "Items",
          icon: Package,
          testId: "nav-item-master-btn",
        },
        {
          path: "/inventory",
          label: "Inventory",
          icon: Boxes,
          testId: "nav-inventory-btn",
        },
        {
          path: "/bank-reconciliation",
          label: "Bank Recon",
          icon: CreditCard,
          testId: "nav-bank-btn",
        },
        ...(user.account_type === "organization"
          ? [
              {
                path: "/team-members",
                label: "My Clients",
                icon: Users,
                testId: "nav-team-members-btn",
              },
            ]
          : []),
        ...(user.account_type !== "team_member"
          ? [
              {
                path: "/settings",
                label: "Settings",
                icon: SettingsIcon,
                testId: "nav-settings-btn",
              },
            ]
          : []),
      ];

  const getActiveClass = (path) => {
    const isActive = location.pathname === path;
    return `w-full items-center flex p-4 py-2 mt-2 rounded ${
      isActive
        ? "bg-gradient-to-r from-primary-500 to-orange-500 text-white"
        : "text-gray-700 hover:text-primary-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50"
    }`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100">
      <nav
        className={`bg-white text-gray-800 shadow-lg border-b border-gray-200 w-full fixed top-0 z-50 transition-all duration-300 ${
          isScrolled ? "shadow-xl" : "shadow-md"
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Left side: Logo and Brand */}
            <div className="flex items-center gap-3 sm:gap-4 lg:gap-6">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50 rounded-lg transition-colors"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? (
                  <X size={22} className="text-gray-700" />
                ) : (
                  <Menu size={22} className="text-gray-700" />
                )}
              </button>

              {/* Company Logo and Name */}
              <div
                className="flex items-center gap-2 sm:gap-3"
                data-testid="app-logo"
              >
                {companySettings ? (
                  <>
                    {/* Company Logo or Placeholder */}
                    {companySettings?.company_logo ? (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <img
                          src={companySettings.company_logo}
                          alt={companySettings.company_name}
                          className="h-8 sm:h-10 w-auto object-contain max-w-[120px] sm:max-w-[160px]"
                          style={{ imageRendering: "crisp-edges" }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 sm:gap-3">
                        {/* Desktop */}
                        <div className="hidden sm:flex items-center gap-3 px-3 py-2 rounded-lg">
                          <div className="bg-gradient-to-r from-primary-500 to-orange-500 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm">
                            <Building2
                              size={16}
                              className="text-white font-bold"
                            />
                          </div>
                          <div>
                            <div className="font-bold text-xs text-gray-800">
                              {companySettings.company_name}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <span>Powered by</span>
                              <span className="font-medium text-primary-600">
                                SmartCA
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Mobile */}
                        <div className="flex sm:hidden items-center gap-2">
                          <div className="bg-gradient-to-r from-primary-500 to-orange-500 w-6 h-6 rounded-md flex items-center justify-center">
                            <Building2 size={12} className="text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-xs text-gray-800 truncate max-w-[120px]">
                              {companySettings.company_name}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              Powered by SmartCA
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Desktop */}
                    <div className="hidden sm:flex items-center gap-3 px-3 py-2 rounded-lg">
                      <div className="bg-gradient-to-r from-primary-500 to-orange-500 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm">
                        <Building2 size={16} className="text-white font-bold" />
                      </div>
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                          <div className="text-xs text-gray-500">
                            Loading...
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="font-bold text-xs bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                            SmartCA
                          </div>
                          <div className="text-xs text-gray-500">
                            by SMART ITBOX
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Mobile */}
                    <div className="flex sm:hidden items-center gap-2 px-2 py-1.5 rounded-lg bg-gradient-to-r from-primary-50/30 to-orange-50/20 border border-primary-100/50">
                      <div className="bg-gradient-to-r from-primary-500 to-orange-500 w-6 h-6 rounded-md flex items-center justify-center shadow-sm">
                        <Building2 size={12} className="text-white font-bold" />
                      </div>
                      <div>
                        <div className="font-bold text-sm bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
                          SmartCA
                        </div>
                        <div className="text-[9px] text-gray-500">
                          by SMART ITBOX
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 ml-4">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  data-testid={item.testId}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium ${
                    location.pathname === item.path
                      ? "bg-gradient-to-r from-primary-500 to-orange-500 text-white"
                      : "text-gray-700 hover:text-primary-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50"
                  }`}
                >
                  <item.icon size={18} />
                  <span className="whitespace-nowrap">{item.label}</span>
                </button>
              ))}
            </div>

            {shouldShowClientWorkspace && !isAdmin && (
              <div className="hidden xl:flex items-center gap-2 min-w-[240px]">
                {accessibleClients.length > 0 ? (
                  <Select value={workspaceSelectValue} onValueChange={handleClientWorkspaceChange}>
                    <SelectTrigger className="h-9 text-xs border-gray-300">
                      <div className="flex items-center gap-2 min-w-0">
                        <Users size={14} className="text-primary-600" />
                        <SelectValue
                          placeholder={
                            user.account_type === "organization"
                              ? "All workspaces"
                              : "Select client workspace"
                          }
                        />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {user.account_type === "organization" && (
                        <SelectItem value="__all__" className="text-xs">
                          All Workspaces
                        </SelectItem>
                      )}
                      {accessibleClients.map((client) => (
                        <SelectItem key={client.id} value={client.id} className="text-xs">
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : user.account_type === "team_member" ? (
                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                    No client assigned
                  </Badge>
                ) : null}
              </div>
            )}

            {/* Right side: User Profile & Logout */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Desktop User Dropdown */}
              <div className="hidden sm:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="group relative flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm bg-white min-w-0 transition-all duration-200">
                      {/* Hover background effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-50/0 to-orange-50/0 group-hover:from-primary-50/30 group-hover:to-orange-50/30 transition-all duration-300 rounded-lg"></div>

                      <div className="relative z-10 flex-1 min-w-0 text-right">
                        <div
                          className="text-xs font-semibold text-gray-800 truncate group-hover:text-primary-700 transition-colors capitalize"
                          data-testid="user-name-display"
                        >
                          {user.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate group-hover:text-gray-600 transition-colors">
                          {user.email}
                        </div>
                      </div>

                      <div className="relative z-10 flex-shrink-0">
                        <div className="relative w-8 h-8 bg-gradient-to-br from-primary-500 to-orange-500 rounded-full flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                          <User size={14} className="text-white" />
                          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border border-white"></div>
                        </div>
                      </div>

                      {/* Active indicator */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-gradient-to-b from-primary-500 to-orange-500 rounded-l opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={() => handleNavigation("/profile")}
                      data-testid="profile-menu-item"
                      className="hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50"
                    >
                      <User size={16} className="mr-2" />
                      My Profile
                    </DropdownMenuItem>
                    {!isAdmin && user.account_type !== "team_member" && (
                      <DropdownMenuItem
                        onClick={() => handleNavigation("/settings")}
                        className="hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50"
                      >
                        <SettingsIcon size={16} className="mr-2" />
                        Company Settings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600 hover:text-red-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-orange-50 focus:text-red-700"
                    >
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile User Info */}
              <div className="sm:hidden flex items-center gap-2">
                <div className="text-right">
                  <div className="text-xs capitalize font-medium text-gray-900 truncate max-w-[120px]">
                    {user.name?.split(" ")[0]}
                  </div>
                </div>
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                  <User size={14} className="text-white" />
                </div>
              </div>

              {/* Logout Button */}
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-btn"
                className="border-gray-300 bg-white text-gray-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-orange-50 hover:text-red-700 hover:border-red-300 transition-all duration-300 hidden sm:flex"
              >
                <LogOut size={16} className="mr-2" />
                <span className="hidden lg:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Sheet */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent
          side="left"
          className="w-[280px] sm:w-[320px] bg-white text-gray-900 border-r border-gray-200 p-0"
        >
          <SheetHeader className="p-6 border-b border-gray-200">
            <div className="pt-4">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-orange-500 rounded-full flex items-center justify-center">
                  <User size={18} className="text-white" />
                </div>
                <div>
                  <div className="font-medium text-xs text-gray-900 text-start capitalize">
                    {user.name}
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                  {isAdmin && (
                    <Badge className="mt-1 bg-gradient-to-r from-primary-500 to-orange-500 text-white text-xs">
                      Admin
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className="py-4 mx-4">
            {shouldShowClientWorkspace && accessibleClients.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">
                  Active Workspace
                </div>
                <Select value={workspaceSelectValue} onValueChange={handleClientWorkspaceChange}>
                  <SelectTrigger className="h-9 text-xs border-gray-300">
                    <SelectValue
                      placeholder={
                        user.account_type === "organization"
                          ? "All workspaces"
                          : "Select client workspace"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {user.account_type === "organization" && (
                      <SelectItem value="__all__" className="text-xs">
                        All Workspaces
                      </SelectItem>
                    )}
                    {accessibleClients.map((client) => (
                      <SelectItem key={client.id} value={client.id} className="text-xs">
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={getActiveClass(item.path)}
                data-testid={`mobile-${item.testId}`}
              >
                <item.icon size={20} />
                <span className="ml-2">{item.label}</span>
              </button>
            ))}

            <div className="mt-6 space-y-2">
              <button
                onClick={() => handleNavigation("/profile")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50 w-full text-left"
              >
                <User size={18} />
                My Profile
              </button>
              {!isAdmin && user.account_type !== "team_member" && (
                <button
                  onClick={() => handleNavigation("/settings")}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-orange-50 w-full text-left"
                >
                  <SettingsIcon size={18} />
                  Company Settings
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:text-red-700 hover:bg-gradient-to-r hover:from-red-50 hover:to-orange-50 w-full text-left border-t border-gray-200 pt-4 mt-4"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 mt-16 sm:mt-20 w-full">
        {children}
      </main>
      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-4 mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            {/* Copyright - Left */}
            <div className="text-center md:text-left">
              <p className="text-xs font-medium text-gray-600">
                © 2026{" "}
                <span className="bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent font-semibold">
                  SMART ITBOX
                </span>
                . All rights reserved.
              </p>
            </div>

            {/* Branding - Center */}
            <div className="flex items-center gap-2">
              <p className="text-xs text-gray-500 text-center leading-relaxed hidden md:block">
                Designed with ❤️ and crafted with care by Team{" "}
                <a
                  href="https://smartitbox.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent hover:from-primary-600 hover:to-orange-600 transition-all"
                >
                  SMART ITBOX
                </a>{" "}
                Your Business Automation Partner
              </p>
              <p className="text-xs text-gray-500 text-center leading-relaxed md:hidden">
                Made with ❤️ by{" "}
                <a
                  href="https://smartitbox.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold bg-gradient-to-r from-primary-500 to-orange-500 bg-clip-text text-transparent"
                >
                  SMART ITBOX
                </a>
              </p>
            </div>

            {/* Footer Links - Right */}
            <div className="hidden md:flex gap-4">
              <a
                href="https://smartitbox.in/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="https://smartitbox.in/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="https://smartitbox.in/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
              >
                Contact
              </a>
            </div>
          </div>

          {/* Mobile Bottom Links */}
          <div className="mt-3 flex justify-center gap-3 md:hidden">
            <a
              href="https://smartitbox.in/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
            >
              Privacy
            </a>
            <span className="text-gray-300">•</span>
            <a
              href="https://smartitbox.in/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
            >
              Terms
            </a>
            <span className="text-gray-300">•</span>
            <a
              href="https://smartitbox.in/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-primary-600 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
